import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import {
    ArrowLeft,
    ArrowUpRight,
    GitCompareArrows,
    Loader2,
    Search as SearchIcon,
    Sparkles,
    X,
} from "lucide-react";

export default function CaseLawExplorer() {
    const { caseId } = useParams();
    const [params] = useSearchParams();
    if (caseId) return <CaseDetail caseId={caseId} />;
    if (params.get("compare") === "1") return <CompareView />;
    return <CasesList />;
}

function CasesList() {
    const [landmarks, setLandmarks] = useState([]);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);

    const [topicFilter, setTopicFilter] = useState("");
    const [courtFilter, setCourtFilter] = useState("");
    const [yearFrom, setYearFrom] = useState("");
    const [yearTo, setYearTo] = useState("");
    const [q, setQ] = useState("");

    // AI semantic search
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState(null);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState("");

    useEffect(() => {
        const params = new URLSearchParams();
        if (topicFilter) params.set("topic", topicFilter);
        if (courtFilter) params.set("court", courtFilter);
        if (yearFrom) params.set("year_from", yearFrom);
        if (yearTo) params.set("year_to", yearTo);
        if (q) params.set("q", q);
        setLoading(true);
        api.get(`/cases/landmarks?${params.toString()}`)
            .then(({ data }) => {
                setLandmarks(data.items || []);
                setTopics(data.topics || []);
            })
            .finally(() => setLoading(false));
    }, [topicFilter, courtFilter, yearFrom, yearTo, q]);

    const runSearch = async (e) => {
        e?.preventDefault();
        const text = searchQuery.trim();
        if (!text) return;
        setSearching(true);
        setSearchError("");
        try {
            const body = { query: text };
            if (courtFilter) body.court = courtFilter;
            if (yearFrom) body.year_from = parseInt(yearFrom, 10);
            if (yearTo) body.year_to = parseInt(yearTo, 10);
            if (topicFilter) body.topic = topicFilter;
            const { data } = await api.post("/cases/search", body);
            setSearchResults(data.items || []);
        } catch (err) {
            const d = err?.response?.data?.detail;
            setSearchError(typeof d === "string" ? d : d?.message || "Search failed.");
        } finally {
            setSearching(false);
        }
    };

    const clearSearch = () => {
        setSearchResults(null);
        setSearchError("");
        setSearchQuery("");
    };

    return (
        <div className="container-edit py-10 lg:py-14">
            <div className="label-eyebrow">Module</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                Case Law Explorer
            </h1>
            <p className="mt-3 max-w-2xl text-[#0D1B3E]/75">
                Search CJEU and ECtHR case law by question, not keyword. Each result is
                a real case with its case number, ECLI, parties, holding and
                significance.
            </p>

            {/* Semantic search */}
            <form
                onSubmit={runSearch}
                className="mt-8 card-flat p-5 flex flex-col gap-3"
                data-testid="case-search-form"
            >
                <div className="flex items-center gap-3">
                    <Sparkles size={14} color="#C9A84C" />
                    <span className="label-eyebrow">AI semantic search</span>
                    <span className="text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55 ml-auto">
                        2 credits
                    </span>
                </div>
                <div className="flex items-end gap-3">
                    <div className="flex-1 relative">
                        <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="e.g. bulk retention of communications data and the Charter"
                            className="field pl-9"
                            data-testid="case-search-input"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={searching || !searchQuery.trim()}
                        className="btn-primary disabled:opacity-50"
                        data-testid="case-search-submit"
                    >
                        {searching ? <Loader2 size={14} className="animate-spin" /> : <SearchIcon size={14} />}
                        Search
                    </button>
                </div>
                {searchError && (
                    <p className="text-sm text-red-700" data-testid="case-search-error">{searchError}</p>
                )}
            </form>

            {/* Filters */}
            <div className="mt-6 grid gap-3 lg:grid-cols-12" data-testid="case-filters">
                <div className="lg:col-span-4 relative">
                    <SearchIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0D1B3E]/50" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Filter landmarks…"
                        className="field pl-9"
                        data-testid="case-grid-q"
                    />
                </div>
                <select
                    className="field lg:col-span-3"
                    value={topicFilter}
                    onChange={(e) => setTopicFilter(e.target.value)}
                    data-testid="case-topic-filter"
                >
                    <option value="">All topics</option>
                    {topics.map((t) => (
                        <option key={t} value={t}>{t}</option>
                    ))}
                </select>
                <select
                    className="field lg:col-span-2"
                    value={courtFilter}
                    onChange={(e) => setCourtFilter(e.target.value)}
                    data-testid="case-court-filter"
                >
                    <option value="">All courts</option>
                    <option value="CJEU">CJEU</option>
                    <option value="ECJ">ECJ</option>
                    <option value="ECtHR">ECtHR</option>
                </select>
                <input
                    type="number"
                    className="field lg:col-span-1"
                    placeholder="From"
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                    data-testid="case-year-from"
                />
                <input
                    type="number"
                    className="field lg:col-span-1"
                    placeholder="To"
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value)}
                    data-testid="case-year-to"
                />
                <Link
                    to="/app/case-law?compare=1"
                    className="btn-secondary lg:col-span-1 justify-center"
                    data-testid="case-compare-link"
                >
                    <GitCompareArrows size={14} /> Compare
                </Link>
            </div>

            {/* Search results override the grid */}
            {searchResults ? (
                <section className="mt-10" data-testid="case-search-results">
                    <div className="flex items-center justify-between">
                        <h2 className="font-display text-2xl tracking-tight">Search results</h2>
                        <button
                            type="button"
                            onClick={clearSearch}
                            className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
                            data-testid="case-search-clear"
                        >
                            <X size={12} /> Clear
                        </button>
                    </div>
                    <ul className="mt-5 grid gap-4 md:grid-cols-2">
                        {searchResults.map((c, i) => (
                            <CaseCard key={`${c.short_title}-${i}`} c={c} isSearchResult />
                        ))}
                    </ul>
                </section>
            ) : (
                <>
                    <div className="mt-10 flex items-end justify-between flex-wrap gap-3">
                        <h2 className="font-display text-2xl tracking-tight">Landmark cases</h2>
                        <span className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/55">
                            {landmarks.length} cases
                        </span>
                    </div>
                    {loading ? (
                        <p className="mt-6 text-[#0D1B3E]/60 flex items-center gap-2">
                            <Loader2 size={16} className="animate-spin" /> Loading…
                        </p>
                    ) : (
                        <ul
                            className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3"
                            data-testid="case-landmark-grid"
                        >
                            {landmarks.map((c) => (
                                <CaseCard key={c.id} c={c} />
                            ))}
                        </ul>
                    )}
                </>
            )}
        </div>
    );
}

function CaseCard({ c, isSearchResult }) {
    const linkable = !isSearchResult || c.in_corpus;
    const to = c.id ? `/app/case-law/${c.id}` : c.corpus_id ? `/app/case-law/${c.corpus_id}` : null;
    return (
        <li
            className="card-flat p-6 flex flex-col gap-3"
            data-testid={`case-card-${(c.id || c.case_number || c.short_title).replace(/[^a-z0-9]+/gi, "-")}`}
        >
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C9A84C]">
                    {c.court} · {c.year}
                </span>
                {isSearchResult && (
                    <span
                        className={`text-[10px] uppercase tracking-[0.2em] ${
                            c.in_corpus ? "text-emerald-700" : "text-[#0D1B3E]/55"
                        }`}
                    >
                        {c.in_corpus ? "Verified corpus" : "Adjacent case"}
                    </span>
                )}
            </div>
            <h3 className="font-display text-xl tracking-tight leading-snug">
                {to && linkable ? (
                    <Link to={to} className="hover:text-[#C9A84C]">{c.short_title}</Link>
                ) : (
                    c.short_title
                )}
            </h3>
            <div className="text-[11px] font-mono text-[#0D1B3E]/65 truncate">
                {c.case_number}
                {c.ecli ? ` · ${c.ecli}` : ""}
            </div>
            <p className="text-sm text-[#0D1B3E]/80 line-clamp-2 italic">{c.parties}</p>
            <p className="text-sm text-[#0D1B3E]/85 leading-relaxed line-clamp-4">{c.holding}</p>
            <div className="flex flex-wrap gap-1.5">
                {(c.topic || []).map((t) => (
                    <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#0D1B3E]/15 text-[#0D1B3E]/70"
                    >
                        {t}
                    </span>
                ))}
            </div>
            <div className="mt-1 flex items-center gap-4">
                {c.url && (
                    <a
                        href={c.url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E] hover:text-[#C9A84C] inline-flex items-center gap-1"
                    >
                        Open <ArrowUpRight size={11} />
                    </a>
                )}
                {to && linkable && (
                    <Link
                        to={to}
                        className="text-xs uppercase tracking-[0.18em] text-[#0D1B3E]/70 hover:text-[#C9A84C]"
                    >
                        Detail →
                    </Link>
                )}
            </div>
        </li>
    );
}

function CaseDetail({ caseId }) {
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/cases/${caseId}`)
            .then(({ data }) => setC(data))
            .catch(() => setC(null))
            .finally(() => setLoading(false));
    }, [caseId]);

    if (loading) {
        return (
            <div className="container-edit py-10">
                <p className="text-[#0D1B3E]/60 flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> Loading…
                </p>
            </div>
        );
    }
    if (!c) {
        return (
            <div className="container-edit py-10">
                <p>Case not found.</p>
                <Link to="/app/case-law" className="btn-secondary mt-6 inline-flex">
                    <ArrowLeft size={14} /> Back
                </Link>
            </div>
        );
    }
    return (
        <div className="container-edit py-10 lg:py-14 max-w-4xl" data-testid="case-detail">
            <button
                type="button"
                onClick={() => navigate("/app/case-law")}
                className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
                data-testid="case-detail-back"
            >
                <ArrowLeft size={14} /> Back to explorer
            </button>

            <div className="mt-6 flex items-center gap-3 flex-wrap">
                <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C9A84C]">
                    {c.court} · {c.year}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55 font-mono">
                    {c.case_number}
                </span>
                {c.ecli && (
                    <span className="text-[10px] uppercase tracking-[0.2em] text-[#0D1B3E]/55 font-mono">
                        {c.ecli}
                    </span>
                )}
            </div>
            <h1 className="font-display text-3xl lg:text-4xl mt-3 tracking-tight" data-testid="case-detail-title">
                {c.short_title}
            </h1>
            <p className="mt-2 text-[#0D1B3E]/80 italic">{c.parties}</p>
            <p className="mt-1 text-sm text-[#0D1B3E]/65">Judgment: {c.date_of_judgment}</p>

            <section className="mt-8">
                <div className="label-eyebrow">Holding</div>
                <p className="mt-2 text-base leading-relaxed">{c.holding}</p>
            </section>
            <section className="mt-7">
                <div className="label-eyebrow">Significance</div>
                <p className="mt-2 text-base leading-relaxed">{c.significance}</p>
            </section>

            <div className="mt-7 flex flex-wrap gap-1.5">
                {(c.topic || []).map((t) => (
                    <span
                        key={t}
                        className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-[#0D1B3E]/15 text-[#0D1B3E]/70"
                    >
                        {t}
                    </span>
                ))}
            </div>

            <div className="mt-10 flex flex-wrap gap-3">
                <a href={c.url} target="_blank" rel="noreferrer" className="btn-primary">
                    Open on curia.europa.eu <ArrowUpRight size={14} />
                </a>
                <Link
                    to={`/app/case-law?compare=1&a=${c.id}`}
                    className="btn-secondary"
                    data-testid="case-detail-compare"
                >
                    <GitCompareArrows size={14} /> Compare with…
                </Link>
            </div>
        </div>
    );
}

function CompareView() {
    const [params] = useSearchParams();
    const [landmarks, setLandmarks] = useState([]);
    const [aId, setAId] = useState(params.get("a") || "");
    const [bId, setBId] = useState(params.get("b") || "");
    const [running, setRunning] = useState(false);
    const [err, setErr] = useState("");
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.get("/cases/landmarks").then(({ data }) => setLandmarks(data.items || []));
    }, []);

    const a = useMemo(() => landmarks.find((x) => x.id === aId), [landmarks, aId]);
    const b = useMemo(() => landmarks.find((x) => x.id === bId), [landmarks, bId]);

    const run = async (e) => {
        e?.preventDefault();
        if (!aId || !bId || aId === bId) {
            setErr("Pick two different cases to compare.");
            return;
        }
        setErr("");
        setRunning(true);
        try {
            const { data } = await api.post("/cases/compare", { case_a_id: aId, case_b_id: bId });
            setResult(data);
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            setErr(typeof d === "string" ? d : d?.message || "Comparison failed.");
        } finally {
            setRunning(false);
        }
    };

    return (
        <div className="container-edit py-10 lg:py-14 max-w-5xl" data-testid="case-compare">
            <Link
                to="/app/case-law"
                className="text-xs uppercase tracking-[0.2em] text-[#0D1B3E]/70 hover:text-[#C9A84C] inline-flex items-center gap-1"
            >
                <ArrowLeft size={14} /> Back to explorer
            </Link>
            <div className="label-eyebrow mt-6">Compare two cases</div>
            <h1 className="font-display text-3xl lg:text-4xl mt-2 tracking-tight">Head to head</h1>
            <p className="mt-2 text-[#0D1B3E]/75 max-w-2xl">
                Pick any two landmark cases and the research engine will compare them on
                court, holding, test applied, common ground, and practical takeaways.
                Heavy work: 3 credits.
            </p>

            <form onSubmit={run} className="mt-7 grid gap-4 md:grid-cols-12 items-end">
                <CasePicker label="Case A" value={aId} onChange={setAId} options={landmarks} testId="case-compare-a" />
                <CasePicker label="Case B" value={bId} onChange={setBId} options={landmarks} testId="case-compare-b" />
                <button
                    type="submit"
                    disabled={running || !aId || !bId}
                    className="btn-primary md:col-span-2 disabled:opacity-50"
                    data-testid="case-compare-run"
                >
                    {running ? <Loader2 size={14} className="animate-spin" /> : <GitCompareArrows size={14} />}
                    Compare
                </button>
            </form>
            {err && <p className="mt-3 text-sm text-red-700" data-testid="case-compare-error">{err}</p>}

            {(a || b) && !result && (
                <div className="mt-8 grid gap-4 md:grid-cols-2 text-sm">
                    {a && <PickerPreview c={a} />}
                    {b && <PickerPreview c={b} />}
                </div>
            )}

            {result && (
                <section className="mt-10" data-testid="case-compare-result">
                    <div className="grid gap-3 md:grid-cols-2 text-sm">
                        <PickerPreview c={result.case_a} />
                        <PickerPreview c={result.case_b} />
                    </div>
                    {result.comparison.question_at_issue && (
                        <p className="mt-7 text-base italic text-[#0D1B3E]/85">
                            {result.comparison.question_at_issue}
                        </p>
                    )}
                    <div className="mt-6 overflow-x-auto">
                        <table className="w-full border-collapse text-sm" data-testid="case-compare-table">
                            <thead>
                                <tr className="text-left">
                                    <th className="border-b border-[#0D1B3E]/15 py-2 px-3 text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                        Dimension
                                    </th>
                                    <th className="border-b border-[#0D1B3E]/15 py-2 px-3 text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                        {result.case_a.short_title}
                                    </th>
                                    <th className="border-b border-[#0D1B3E]/15 py-2 px-3 text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55">
                                        {result.case_b.short_title}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {result.comparison.rows.map((r, i) => (
                                    <tr key={i} className="align-top">
                                        <td className="border-b border-[#0D1B3E]/10 py-3 px-3 font-display">
                                            {r.dimension}
                                        </td>
                                        <td className="border-b border-[#0D1B3E]/10 py-3 px-3 text-[#0D1B3E]/85">
                                            {r.a}
                                        </td>
                                        <td className="border-b border-[#0D1B3E]/10 py-3 px-3 text-[#0D1B3E]/85">
                                            {r.b}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {result.comparison.common_ground?.length > 0 && (
                        <Block title="Common ground" items={result.comparison.common_ground} />
                    )}
                    {result.comparison.key_differences?.length > 0 && (
                        <Block title="Key differences" items={result.comparison.key_differences} />
                    )}
                    {result.comparison.practical_takeaways?.length > 0 && (
                        <Block title="Practical takeaways" items={result.comparison.practical_takeaways} />
                    )}
                </section>
            )}
        </div>
    );
}

function CasePicker({ label, value, onChange, options, testId }) {
    return (
        <label className="md:col-span-5 block">
            <span className="block text-[10px] uppercase tracking-[0.22em] text-[#0D1B3E]/55 mb-1">
                {label}
            </span>
            <select
                className="field"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            >
                <option value="">Pick a case…</option>
                {options.map((c) => (
                    <option key={c.id} value={c.id}>
                        {`${c.short_title} · ${c.case_number}`}
                    </option>
                ))}
            </select>
        </label>
    );
}

function PickerPreview({ c }) {
    return (
        <div className="card-flat p-4">
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#C9A84C]">
                {c.court} · {c.year}
            </div>
            <div className="font-display text-lg mt-1">{c.short_title}</div>
            <div className="text-[11px] font-mono text-[#0D1B3E]/65">{c.case_number}</div>
            <p className="mt-2 text-sm text-[#0D1B3E]/80 line-clamp-3">{c.holding}</p>
        </div>
    );
}

function Block({ title, items }) {
    return (
        <div className="mt-7">
            <div className="label-eyebrow mb-2">{title}</div>
            <ul className="list-disc pl-5 space-y-1.5 text-sm text-[#0D1B3E]/85">
                {items.map((x, i) => <li key={i}>{x}</li>)}
            </ul>
        </div>
    );
}
